/**
 * @module AdaptersPage
 * @package @geenius/adapters/solidjs-kobalte
 * @description Kobalte-backed adapters overview page for the SolidJS library
 * variant.
 */

import type {
  AdapterDomain,
  AdapterStatusType,
  DomainAdapterConfig,
} from "@geenius/adapters";
import {
  ADAPTER_DOMAINS,
  ADAPTER_STATUSES,
  getDomainConfig,
  isAdaptersConfigured,
} from "@geenius/adapters";
import { createAdapters } from "@geenius/adapters/solidjs";
import type { JSX } from "solid-js";
import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import {
  AdapterConfigForm,
  AdapterList,
  AdapterStatusBadge,
} from "../components";
import { cn, loadKobalteCore } from "../lib/kobalte";

type RegistryEntry = {
  domain: AdapterDomain;
  provider: string;
  status: AdapterStatusType;
};

export interface AdaptersPageProps {
  onNavigateDetail?: (domain: AdapterDomain) => void;
  onConfigure?: (
    domain: AdapterDomain,
    config: DomainAdapterConfig,
  ) => Promise<void> | void;
  class?: string;
  configByDomain?: Partial<Record<AdapterDomain, DomainAdapterConfig>>;
  registryEntries?: RegistryEntry[];
}

export function AdaptersPage(props: AdaptersPageProps): JSX.Element {
  const ctx = createAdapters();
  const [core] = createResource(loadKobalteCore);
  const Button = createMemo(() => core()?.Button?.Root);
  const Dialog = createMemo(() => core()?.Dialog);
  const [selectedDomain, setSelectedDomain] =
    createSignal<AdapterDomain | null>(null);
  const [filterStatus, setFilterStatus] = createSignal<
    AdapterStatusType | undefined
  >();
  const registryEntries = () => props.registryEntries ?? [];
  const connected = () =>
    ADAPTER_DOMAINS.filter(
      (domain) => ctx.statuses[domain]?.status === "connected",
    ).length;
  const errors = () =>
    ADAPTER_DOMAINS.filter((domain) => ctx.statuses[domain]?.status === "error")
      .length;
  const initialConfigFor = (domain: AdapterDomain) =>
    props.configByDomain?.[domain] ??
    (isAdaptersConfigured() ? getDomainConfig(domain) : undefined) ??
    undefined;

  const filterButtonClass = (active: boolean) =>
    cn(
      "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/30",
      active
        ? "border-sky-500 bg-sky-50 text-sky-700"
        : "border-slate-200 bg-white text-slate-500 hover:text-slate-950",
    );

  const renderButton = (buttonProps: {
    children: JSX.Element;
    class: string;
    onClick: () => void;
    pressed: boolean;
  }): JSX.Element => (
    <Show
      fallback={
        <button
          aria-pressed={buttonProps.pressed}
          class={buttonProps.class}
          onClick={buttonProps.onClick}
          type="button"
        >
          {buttonProps.children}
        </button>
      }
      keyed
      when={Button()}
    >
      {(KobalteButton) => (
        <KobalteButton
          aria-pressed={buttonProps.pressed}
          class={buttonProps.class}
          onClick={buttonProps.onClick}
          type="button"
        >
          {buttonProps.children}
        </KobalteButton>
      )}
    </Show>
  );

  const configForm = (domain: AdapterDomain) => (
    <AdapterConfigForm
      domain={domain}
      initialConfig={initialConfigFor(domain)}
      onCancel={() => setSelectedDomain(null)}
      onSave={async (config) => {
        await props.onConfigure?.(domain, config);
        setSelectedDomain(null);
      }}
    />
  );

  const fallbackDialog = (domain: AdapterDomain) => (
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4 backdrop-blur-sm">
      <button
        aria-label="Close adapter configuration"
        class="absolute inset-0 cursor-default border-0 bg-transparent p-0"
        onClick={() => setSelectedDomain(null)}
        type="button"
      />
      <div
        aria-modal="true"
        class="relative z-10 w-full max-w-lg"
        role="dialog"
      >
        {configForm(domain)}
      </div>
    </div>
  );

  return (
    <Show
      fallback={
        <div class={cn("mx-auto max-w-4xl space-y-6", props.class)}>
          <div
            aria-label="Loading adapters"
            class="animate-pulse space-y-4"
            role="status"
          >
            <div class="h-8 w-48 rounded bg-slate-100" />
            <div class="h-4 w-72 rounded bg-slate-100" />
            <div class="grid gap-3 sm:grid-cols-3">
              <For each={[1, 2, 3]}>
                {() => (
                  <div class="h-20 rounded-lg border border-slate-200 bg-white" />
                )}
              </For>
            </div>
            <div class="grid gap-3 sm:grid-cols-2">
              <For each={[1, 2, 3, 4]}>
                {() => (
                  <div class="h-28 rounded-lg border border-slate-200 bg-white" />
                )}
              </For>
            </div>
          </div>
        </div>
      }
      when={!ctx.isLoading}
    >
      <div
        class={cn("mx-auto max-w-4xl space-y-8 text-slate-950", props.class)}
        data-geenius-component="AdaptersPage"
        data-geenius-variant="solidjs-kobalte"
      >
        <header class="space-y-1">
          <h1 class="text-2xl font-extrabold text-slate-950">Adapters</h1>
          <p class="text-sm text-slate-500">
            Manage your infrastructure adapters - database, auth, AI, storage,
            payments, and admin.
          </p>
        </header>

        <section class="grid gap-3 sm:grid-cols-3">
          <div class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <p class="text-xs font-semibold uppercase text-slate-500">Total</p>
            <p class="mt-1 text-2xl font-extrabold text-slate-950">
              {ADAPTER_DOMAINS.length}
            </p>
            <p class="text-[0.625rem] text-slate-500">adapter domains</p>
          </div>
          <div class="rounded-lg border border-emerald-500/20 bg-emerald-50 p-4 shadow-sm">
            <p class="text-xs font-semibold uppercase text-emerald-700">
              Connected
            </p>
            <p class="mt-1 text-2xl font-extrabold text-emerald-700">
              {connected()}
            </p>
            <p class="text-[0.625rem] text-emerald-700/70">active adapters</p>
          </div>
          <Show when={errors() > 0}>
            <div class="rounded-lg border border-red-500/20 bg-red-50 p-4 shadow-sm">
              <p class="text-xs font-semibold uppercase text-red-700">Errors</p>
              <p class="mt-1 text-2xl font-extrabold text-red-700">
                {errors()}
              </p>
              <p class="text-[0.625rem] text-red-700/70">need attention</p>
            </div>
          </Show>
        </section>

        <Show when={registryEntries().length > 0}>
          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p class="text-xs font-semibold uppercase text-slate-500">
                  Adapter Registry
                </p>
                <p class="text-sm text-slate-500">
                  Live adapter registry entries.
                </p>
              </div>
              <div class="flex gap-3 text-xs text-slate-500">
                <span>{registryEntries().length} entries</span>
              </div>
            </div>
            <div class="mt-4 grid gap-2 sm:grid-cols-2">
              <For each={registryEntries()}>
                {(entry) => (
                  <div class="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
                    <div class="flex items-center justify-between gap-3">
                      <div>
                        <p class="text-sm font-semibold text-slate-950">
                          {entry.domain}
                        </p>
                        <p class="text-xs text-slate-500">{entry.provider}</p>
                      </div>
                      <AdapterStatusBadge status={entry.status} />
                    </div>
                  </div>
                )}
              </For>
            </div>
          </section>
        </Show>

        <section class="flex flex-wrap items-center gap-2">
          {renderButton({
            class: filterButtonClass(!filterStatus()) ?? "",
            onClick: () => setFilterStatus(undefined),
            pressed: !filterStatus(),
            children: "All",
          })}
          <For each={[...ADAPTER_STATUSES]}>
            {(status) =>
              renderButton({
                class: filterButtonClass(filterStatus() === status) ?? "",
                onClick: () => setFilterStatus(status),
                pressed: filterStatus() === status,
                children: (
                  <>
                    <AdapterStatusBadge status={status} showLabel={false} />
                    {status}
                  </>
                ),
              })
            }
          </For>
        </section>

        <AdapterList
          filterStatus={filterStatus()}
          onSelect={(domain) => {
            if (props.onNavigateDetail) {
              props.onNavigateDetail(domain);
            } else {
              setSelectedDomain(domain);
            }
          }}
        />

        <Show when={selectedDomain()}>
          {(domain) => (
            <Show fallback={fallbackDialog(domain())} keyed when={Dialog()}>
              {(KobalteDialog) => (
                <KobalteDialog.Root
                  modal
                  onOpenChange={(open) => {
                    if (!open) {
                      setSelectedDomain(null);
                    }
                  }}
                  open
                >
                  <KobalteDialog.Portal>
                    <KobalteDialog.Overlay class="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm" />
                    <div class="fixed inset-0 z-50 flex items-center justify-center px-4">
                      <KobalteDialog.Content class="w-full max-w-lg outline-none">
                        <KobalteDialog.Title class="sr-only">
                          Configure {domain()} Adapter
                        </KobalteDialog.Title>
                        {configForm(domain())}
                      </KobalteDialog.Content>
                    </div>
                  </KobalteDialog.Portal>
                </KobalteDialog.Root>
              )}
            </Show>
          )}
        </Show>
      </div>
    </Show>
  );
}
